import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '../App';
import { Plus, Trash2, Upload, Save, Sparkles, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import './PuzzleBuilder.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PuzzleBuilder() {
  const { t } = useTranslation();
  const { user } = React.useContext(AuthContext);
  const [puzzleData, setPuzzleData] = useState({
    title: '',
    puzzle_image_url: '',
    grid_size: 9,
    timer_setting: 'off',
    squares: Array(9).fill(null).map(() => ({ question: '', answer: '' }))
  });
  
  const [previewImage, setPreviewImage] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  
  const handleImageUpload = async (file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setPuzzleData({ ...puzzleData, puzzle_image_url: response.data.url });
      setPreviewImage(response.data.url);
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
    }
  };
  
  const handleGridSizeChange = (newSize) => {
    const size = parseInt(newSize);
    const newSquares = Array(size).fill(null).map((_, i) => 
      puzzleData.squares[i] || { question: '', answer: '' }
    );
    setPuzzleData({ ...puzzleData, grid_size: size, squares: newSquares });
  };
  
  const updateSquare = (index, field, value) => {
    const newSquares = [...puzzleData.squares];
    newSquares[index][field] = value;
    setPuzzleData({ ...puzzleData, squares: newSquares });
  };
  
  const savePuzzle = async () => {
    if (!puzzleData.title) {
      toast.error('Please enter a puzzle title');
      return;
    }
    
    if (!puzzleData.puzzle_image_url) {
      toast.error('Please upload a puzzle image');
      return;
    }
    
    if (puzzleData.squares.some(sq => !sq.question || !sq.answer)) {
      toast.error('Please fill in all questions and answers');
      return;
    }
    
    try {
      await axios.post(`${API}/puzzles`, puzzleData, { withCredentials: true });
      
      toast.success('Puzzle saved successfully!');
      setPuzzleData({
        title: '',
        puzzle_image_url: '',
        grid_size: 9,
        timer_setting: 'off',
        squares: Array(9).fill(null).map(() => ({ question: '', answer: '' }))
      });
      setPreviewImage(null);
    } catch (error) {
      console.error('Failed to save puzzle:', error);
      toast.error('Failed to save puzzle');
    }
  };
  
  const generateAISquares = async () => {
    if (!aiTopic) {
      toast.error('Please enter a topic');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-puzzle`, {
        topic: aiTopic,
        num_questions: puzzleData.grid_size
      }, { withCredentials: true });
      
      toast.success('AI Squares generated successfully!');
      setAiDialogOpen(false);
      setAiTopic('');
      
      setPuzzleData({
        ...puzzleData,
        squares: response.data.squares
      });
      
    } catch (error) {
      console.error('AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate squares');
    } finally {
      setGenerating(false);
    }
  };
  
  const getGridLabel = (size) => {
    if (size === 9) return '3×3 (9 squares)';
    if (size === 16) return '4×4 (16 squares)';
    if (size === 25) return '5×5 (25 squares)';
    return size;
  };
  
  return (
    <div className="puzzle-builder-page">
      <div className="builder-container">
        <div className="builder-header">
          <h1>🧩 Puzzle Builder</h1>
          <div className="header-actions">
            <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
              <DialogTrigger asChild>
                <Button className="ai-btn">
                  <Sparkles size={20} />
                  AI Square Generator
                </Button>
              </DialogTrigger>
              <DialogContent className="ai-dialog">
                <DialogHeader>
                  <DialogTitle>AI Square Generator</DialogTitle>
                </DialogHeader>
                
                <div className="ai-form">
                  <div className="form-group">
                    <Label>Topic (any language)</Label>
                    <Input
                      value={aiTopic}
                      onChange={(e) => setAiTopic(e.target.value)}
                      placeholder="e.g., Animals, Math, Colors..."
                    />
                  </div>
                  
                  <Button
                    onClick={generateAISquares}
                    disabled={generating}
                    className="generate-btn"
                  >
                    {generating ? 'Generating...' : `Generate ${puzzleData.grid_size} Squares`}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Button onClick={savePuzzle} className="save-btn">
              <Save size={20} />
              Save Puzzle
            </Button>
          </div>
        </div>
        
        <div className="puzzle-settings">
          <Input
            value={puzzleData.title}
            onChange={(e) => setPuzzleData({ ...puzzleData, title: e.target.value })}
            placeholder="Puzzle Title"
            className="title-input"
          />
          
          <div className="settings-row">
            <div className="setting-group">
              <Label>Grid Size</Label>
              <Select
                value={puzzleData.grid_size.toString()}
                onValueChange={handleGridSizeChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="9">{getGridLabel(9)}</SelectItem>
                  <SelectItem value="16">{getGridLabel(16)}</SelectItem>
                  <SelectItem value="25">{getGridLabel(25)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="setting-group">
              <Label>Timer</Label>
              <Select
                value={puzzleData.timer_setting}
                onValueChange={(value) => setPuzzleData({ ...puzzleData, timer_setting: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">OFF</SelectItem>
                  <SelectItem value="30s">30 seconds</SelectItem>
                  <SelectItem value="1min">1 minute</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="image-upload-section">
            <Label>Puzzle Image</Label>
            <input
              type="file"
              accept="image/*"
              id="puzzle-image"
              onChange={(e) => handleImageUpload(e.target.files[0])}
              style={{ display: 'none' }}
            />
            <label htmlFor="puzzle-image" className="upload-btn-large">
              <ImageIcon size={24} />
              {previewImage ? 'Change Puzzle Image' : 'Upload Puzzle Image'}
            </label>
            {previewImage && (
              <div className="image-preview">
                <img src={previewImage} alt="Puzzle" />
              </div>
            )}
          </div>
        </div>
        
        <div className="squares-section">
          <h2>Puzzle Squares ({puzzleData.grid_size} total)</h2>
          <div className="squares-grid">
            {puzzleData.squares.map((square, index) => (
              <div key={index} className="square-card">
                <div className="square-header">
                  <span className="square-number">Square {index + 1}</span>
                </div>
                
                <div className="square-content">
                  <div className="form-group">
                    <Label>Question</Label>
                    <Input
                      value={square.question}
                      onChange={(e) => updateSquare(index, 'question', e.target.value)}
                      placeholder="Enter question"
                    />
                  </div>
                  
                  <div className="form-group">
                    <Label>Answer (Short text)</Label>
                    <Input
                      value={square.answer}
                      onChange={(e) => updateSquare(index, 'answer', e.target.value)}
                      placeholder="Enter answer (1-2 words)"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PuzzleBuilder;